import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from '../renderer/Camera';
import type { RenderPipeline } from '../renderer/RenderPipeline';
import type { WorldInfo } from '../config/WorldConfig';
import type { SupabaseClient } from '../network/SupabaseClient';
import type { StellarRenderConfig } from '../config/StellarRenderConfig';
import type { FogConfig } from '../config/FogConfig';
import { ClusterManager } from './ClusterManager';
import { ContentManager } from './ContentManager';
import { FogManager } from './FogManager';
import { FogState } from './FogState';
import { HexTileRenderer } from '../objects/HexTileRenderer';
import { StarfieldRenderer } from '../objects/StarfieldRenderer';
import { FogRenderer } from '../objects/FogRenderer';

const ZOOM_REBUILD_THRESHOLD = 3;

/**
 * Coordinates hex grid, cluster loading, stellar content, and fog of war.
 * Wires FogState into ContentManager (visibility filter) and
 * ContentManager's sun positions into FogManager (sun exclusion).
 */
export class WorldManager implements GameModule {
  readonly name = 'WorldManager';
  readonly clusterManager: ClusterManager;
  readonly hexRenderer: HexTileRenderer;
  readonly contentManager: ContentManager | null;
  readonly fogManager: FogManager | null;
  readonly fogState: FogState | null;
  readonly worldInfo: WorldInfo;
  private _starfield: StarfieldRenderer;

  private _camera: Camera;
  private _transitionSpeed: number;
  private _lastZoom = -1;
  private _lastTx = -9999;
  private _lastTz = -9999;

  constructor(
    camera: Camera,
    pipeline: RenderPipeline,
    gl: WebGL2RenderingContext,
    worldInfo: WorldInfo,
    transitionSpeed: number,
    supabase?: SupabaseClient,
    worldId?: string,
    stellarConfig?: StellarRenderConfig,
    fogConfig?: FogConfig,
  ) {
    this._camera = camera;
    this._transitionSpeed = transitionSpeed;
    this.worldInfo = worldInfo;
    this.clusterManager = new ClusterManager(camera, worldInfo);
    this._starfield = new StarfieldRenderer(gl);
    pipeline.addRenderable(this._starfield);
    this.hexRenderer = new HexTileRenderer(gl, worldInfo.config);
    pipeline.addRenderable(this.hexRenderer);

    this.fogState = fogConfig?.enabled && worldId
      ? new FogState(supabase ?? null, worldId)
      : null;

    const isRevealed = this.fogState
      ? (q: number, r: number) => this.fogState!.isRevealed(q, r)
      : undefined;

    if (supabase && worldId && stellarConfig) {
      this.contentManager = new ContentManager(
        gl, supabase, worldId, this.clusterManager,
        pipeline, stellarConfig, worldInfo.config.hexRadius, 1,
        isRevealed,
      );
    } else {
      this.contentManager = null;
    }

    if (fogConfig?.enabled && worldId && this.fogState) {
      const fogRenderer = new FogRenderer(
        gl, worldInfo.config.hexRadius, fogConfig.overlay,
      );
      pipeline.addRenderable(fogRenderer);

      const cm = this.contentManager;
      this.fogManager = new FogManager(
        this.clusterManager, fogRenderer, this.fogState,
        worldInfo.config,
        cm ? () => cm.sunHexKeys : null,
        cm ? () => cm.rebuildVersion : null,
      );
    } else {
      this.fogManager = null;
    }
  }

  init(engine: Engine): void {
    this.clusterManager.init(engine);
    this.contentManager?.init(engine);
    this.fogManager?.init(engine);

    engine.events.on('world:clusters-changed', () => {
      this._rebuild();
    });
    engine.events.on<null>('fleet:enter-flightview', () => {
      this.hexRenderer.alpha = 0.015;
    });
    engine.events.on<null>('fleet:exit-flightview', () => {
      this.hexRenderer.alpha = 0.55;
    });
    engine.events.on<null>('planet:hub-opened', () => {
      this.hexRenderer.alpha = 0.04;
    });
    engine.events.on<null>('planet:hub-closed', () => {
      this.hexRenderer.alpha = 0.55;
    });
  }

  update(dt: number): void {
    this.clusterManager.update(dt);
    this.contentManager?.update(dt);
    this.fogManager?.update(dt);
    this.hexRenderer.lerpAlpha(dt, this._transitionSpeed);

    const zoom = this._camera.position.y;
    const tx = this._camera.target.x;
    const tz = this._camera.target.z;

    const panDistSq = (tx - this._lastTx) ** 2 + (tz - this._lastTz) ** 2;

    if (Math.abs(zoom - this._lastZoom) > ZOOM_REBUILD_THRESHOLD || panDistSq > 400) {
      this._rebuild();
      this._lastTx = tx;
      this._lastTz = tz;
    }
  }

  dispose(): void {
    this.clusterManager.dispose();
    this.contentManager?.dispose();
    this.fogManager?.dispose();
    this._starfield.dispose();
    this.hexRenderer.dispose();
  }

  private _rebuild(): void {
    this._lastZoom = this._camera.position.y;
    this.hexRenderer.rebuildFromManager(this.clusterManager, this._camera);
  }
}
